import React, { FC, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Route, Switch, useHistory } from "react-router";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import MessageEventModal from "go-component/components/MessageEvent/MessageEventModal";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import AppFormatResourceStatusLabel from "../../../../../../../../../../components/FormatStatus";
import { TransferStatus } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import CopyDocumentModal from "../../../../components/CopyDocumentModal";
import { TransferContext } from "../../pages/Preview";
import SubmenuTransferPreviewNavigation from "../SubmenuTransferPreviewNavigation";
import TransferPreviewTab from "../Tabs/TransferPreviewTab";

interface Props {
	path: string;
}

const TransferPreview: FC<Props> = ({ path }) => {
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const [showLogs, setShowLogs] = useState<boolean>(false);
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const params = { include: "all" };
	const history = useHistory();
	const { transfer, setTransfer } = useContext(TransferContext);
	const [showCopyModal, setShowCopyModal] = useState(false);
	const { isMobile } = useWindowSize();

	const avatar = {
		color: "#FF9900",
		date: transfer.transferred_at,
	};

	const renderSubtitle = () => (
		<ul>
			<li>{FormatDate(transfer.transferred_at)}</li>
			<li>{FormatMoney(transfer.total_price_net_money)}</li>
		</ul>
	);

	const renderTitle = () => (
		<>
			{t("modules.transfer.header.preview.title")} {transfer.document_number?.replace(" ", "-")}
			<AppFormatResourceStatusLabel status={transfer.status} />
		</>
	);

	const onConfirmTransfer = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().confirmTransfer(transfer.id, params);
			setTransfer(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onCloseTransfer = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().closeTransfer(transfer.id, params);
			setTransfer(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onCancelTransferCancellation = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().closeCancelTransfer(transfer.id, params);
			setTransfer(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onCancelTransfer = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().cancelTransfer(transfer.id, params);
			setTransfer(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onVoidTransfer = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().removeTransfer(transfer.id, params);
			setTransfer(res);
			addSuccessFlash(t("lib:common.flash.completed"));
			history.push(`/${organization.id}/documents/transfers`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onRestoreTransfer = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.activate", { ns: "lib" }),
			});
			const res = await api.organization().restoreTransfer(transfer.id, params);
			setTransfer(res);
			addSuccessFlash(t("lib:common.flash.restored"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const buttons: ButtonProps[] = [
		{
			title: t("lib:common.action.edit"),
			variant: "primary",
			path: `/${organization.id}/documents/transfers/${transfer.id}/edit`,
		},
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(!showLogs),
			dropdown: true,
		},
		{
			title: t("lib:common.action.copy"),
			action: () => setShowCopyModal(true),
			dropdown: true,
		},
	];
	if (transfer.status === TransferStatus.DRAFT)
		buttons.push({
			title: t("lib:common.action.confirm"),
			action: () => onConfirmTransfer(),
			variant: "primary",
		});
	if (transfer.status === TransferStatus.OPEN)
		buttons.push({
			title: t("lib:common.action.close"),
			action: () => onCloseTransfer(),
			variant: "primary",
		});
	if (transfer.status === TransferStatus.VOID)
		buttons.push({
			title: t("common.action.cancel_close"),
			action: () => onCancelTransferCancellation(),
			dropdown: true,
		});
	if (transfer.status === TransferStatus.RECEIVED)
		buttons.push({
			title: t("lib:common.action.cancel"),
			action: () => onCancelTransferCancellation(),
			dropdown: true,
		});
	if (transfer.status === TransferStatus.OPEN)
		buttons.push({
			title: t("lib:common.action.cancel"),
			action: () => onCancelTransfer(),
			dropdown: true,
		});
	if (transfer.status === TransferStatus.OPEN || transfer.status === TransferStatus.RECEIVED)
		buttons.push({
			title: t("common.action.void"),
			action: () => onVoidTransfer(),
			dropdown: true,
		});
	if (transfer.status === TransferStatus.VOID)
		buttons.push({
			title: t("lib:common.action.restore"),
			action: () => onRestoreTransfer(),
			dropdown: true,
		});
	if (transfer.status === TransferStatus.DRAFT || transfer.status === TransferStatus.VOID)
		buttons.push({
			title: t("lib:common.action.remove"),
			action: () => onVoidTransfer(),
			dropdown: true,
		});

	const mobileActions: MobileActionProps[] = [
		{
			title: t("lib:common.action.edit"),
			path: `/${organization.id}/documents/transfers/${transfer.id}/edit`,
		},
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(!showLogs),
		},
		{
			title: t("lib:common.action.copy"),
			action: () => setShowCopyModal(true),
		},
		{
			title: t("lib:common.action.confirm"),
			action: () => onConfirmTransfer(),
			hide: transfer.status !== TransferStatus.DRAFT,
		},
		{
			title: t("lib:common.action.close"),
			action: () => onCloseTransfer(),
			hide: transfer.status !== TransferStatus.OPEN,
		},
		{
			title: t("common.action.cancel_close"),
			action: () => onCancelTransferCancellation(),
			hide: transfer.status !== TransferStatus.VOID,
		},
		{
			title: t("lib:common.action.cancel"),
			action: () => onCancelTransferCancellation(),
			hide: transfer.status !== TransferStatus.RECEIVED,
		},
		{
			title: t("lib:common.action.cancel"),
			action: () => onCancelTransfer(),
			hide: transfer.status !== TransferStatus.OPEN,
		},
		{
			title: t("common.action.void"),
			action: () => onVoidTransfer(),
			hide: transfer.status !== TransferStatus.OPEN && transfer.status !== TransferStatus.RECEIVED,
		},
		{
			title: t("lib:common.action.restore"),
			action: () => onRestoreTransfer(),
			hide: transfer.status !== TransferStatus.VOID,
		},
		{
			title: t("lib:common.action.remove"),
			action: () => onVoidTransfer(),
			hide: transfer.status !== TransferStatus.DRAFT && transfer.status !== TransferStatus.VOID,
		},
	];

	if (!transfer) return <LoadingContainer />;

	return (
		<div className="content-preview">
			<MobileActions actions={mobileActions} />
			<Header
				title={renderTitle()}
				buttons={isMobile ? [] : buttons}
				avatar={avatar}
				subtitle={renderSubtitle()}
				tags={transfer?.tags?.map((tag) => tag.name)}
			/>
			<div className="content-preview-body">
				<SubmenuTransferPreviewNavigation transferId={transfer.id} />
				<Switch>
					<Route path={path} exact component={TransferPreviewTab} />
					{/* <Route path={path + "/transactions"} exact component={OrganizationTransactionIndexPage} /> */}
				</Switch>
			</div>
			{showLogs && (
				<MessageEventModal
					path={`/${organization.id}/logs/message_events`}
					resourceId={transfer.id}
					resourceType="TRANSFER_ORDER"
					onHide={() => setShowLogs(!showLogs)}
					organizationId={organization.id}
				/>
			)}
			{showCopyModal && (
				<CopyDocumentModal
					show={showCopyModal}
					onHide={() => setShowCopyModal(false)}
					document={transfer}
					type="transfer"
				/>
			)}
		</div>
	);
};

export default TransferPreview;
